import { ErrorTypeEnum } from "@/graphql/codegen/graphql"

/**
 * Contract definition for a GraphQL operation the backend has not shipped yet.
 *
 * One contract feeds three consumers:
 *   - the dev-only mockFetch transport (so the UI can be built now)
 *   - Storybook decorator args
 *   - the failing backend resolver specs generated at `--promote` time
 *
 * Contracts live in the frontend and are typed by the frontend's own generated
 * types, so there is no cross-package copy and nothing to code-generate. The
 * proposed schema they depend on lives in src/graphql/proposed/.
 */

export type ContractStatus = "mocked" | "implemented" | "retired"

/**
 * A GraphQL response carries only the document's selection set, so a mock
 * payload is partial by nature. DeepPartial keeps payloads authorable while
 * `satisfies`-style assignability still catches renamed, retyped, and
 * misspelled fields. It cannot detect a field that became *required* upstream.
 */
export type DeepPartial<T> = T extends (infer U)[]
  ? DeepPartial<U>[]
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T

/** Top-level GraphQL error, shaped per the backend's errors.graphql extensions. */
export interface GraphqlErrorSpec {
  code: ErrorTypeEnum
  message: string
  /** Must match the @constraint pattern on the corresponding *Extensions type. */
  status: 400 | 401 | 403 | 500
  instruction?: string
}

export interface ScenarioSpec<TOutput> {
  /**
   * Becomes the `it(...)` description in the generated resolver spec, so write
   * it in house style: present tense, no "should".
   */
  description: string
  /** Exactly one of payload, graphqlError, or extends is required. */
  payload?: () => DeepPartial<TOutput>
  graphqlError?: GraphqlErrorSpec
  extends?: string
  /** Artificial latency applied by mockFetch. Ignored by generated specs. */
  delayMs?: number
  /** Variables that select this scenario. Defaults to the contract-level variables. */
  variables?: Record<string, unknown>
  /** Frontend-only (network:*) — no resolver behaviour to assert, so no spec is generated. */
  frontendOnly?: boolean
}

export interface Contract<TOutput> {
  /** GraphQL operation name — must match the `query X {...}` name in the document. */
  operation: string
  /** Resolver dot path, e.g. "order.get". Builds the response envelope. */
  field: string
  ticket: string
  status: ContractStatus
  owners: { backend: string; frontend: string }
  variables: Record<string, unknown>
  scenarios: Record<string, ScenarioSpec<TOutput>>
}

const ERROR_CODES = new Set<string>(Object.values(ErrorTypeEnum))

const SCENARIO_KEY = /^(?:success(?::[a-zA-Z]\w*)?|error:[A-Z_]+|network:(?:slow|offline))$/

/** Validates at module load so a malformed contract fails loudly, not silently. */
export const defineContract = <TOutput>(contract: Contract<TOutput>): Contract<TOutput> => {
  const where = `contract ${contract.operation}`

  if (!/^[A-Z][A-Za-z0-9]*$/.test(contract.operation)) {
    throw new Error(`${where}: operation must be PascalCase`)
  }
  if (!/^[a-z][A-Za-z0-9]*\.[a-z][A-Za-z0-9]*$/.test(contract.field)) {
    throw new Error(`${where}: field must be a dot path like "order.get"`)
  }
  if (!contract.scenarios.success) {
    throw new Error(`${where}: a "success" scenario is required`)
  }

  for (const [key, scenario] of Object.entries(contract.scenarios)) {
    if (!SCENARIO_KEY.test(key)) {
      throw new Error(`${where}: invalid scenario key "${key}" — see .claude/skills/mock-api/reference/conventions.md`)
    }

    // Only a top-level GraphQL error carries an ErrorTypeEnum code. A union
    // failure has no code field, so domain outcomes like NOT_FOUND are valid
    // there and have no enum equivalent.
    if (key.startsWith("error:") && scenario.graphqlError) {
      const code = key.slice("error:".length)
      if (!ERROR_CODES.has(code)) {
        throw new Error(
          `${where}: scenario "${key}" emits a top-level graphqlError, so its key must name an ErrorTypeEnum member. Valid: ${[...ERROR_CODES].join(", ")}. For a domain failure inside the union, use payload instead.`
        )
      }
      if (scenario.graphqlError.code !== code) {
        throw new Error(
          `${where}: scenario "${key}" declares code "${scenario.graphqlError.code}" — key and code must match`
        )
      }
    }

    const outcomes = [scenario.payload, scenario.graphqlError, scenario.extends].filter(Boolean)
    if (outcomes.length !== 1) {
      throw new Error(`${where}: scenario "${key}" must declare exactly one of payload, graphqlError, or extends`)
    }
    if (scenario.extends && !contract.scenarios[scenario.extends]) {
      throw new Error(`${where}: scenario "${key}" extends "${scenario.extends}", which does not exist`)
    }
    if (scenario.extends && contract.scenarios[scenario.extends].extends) {
      throw new Error(`${where}: scenario "${key}" extends a scenario that itself extends — keep it one level`)
    }
  }

  return contract
}

/** Nests a payload inside the response envelope implied by `field`. */
const wrapPayload = (field: string, payload: unknown): Record<string, unknown> =>
  field
    .split(".")
    .reduceRight<Record<string, unknown>>((acc, segment) => ({ [segment]: acc }), payload as Record<string, unknown>)

export interface ResolvedScenario {
  body?: { data?: unknown; errors?: unknown[] }
  networkError?: string
  delayMs: number
}

/**
 * Resolves a scenario to a spec-compliant GraphQL response body, following one
 * level of `extends`. Returning a real response body — rather than a
 * hand-built CombinedError — lets urql construct the error itself, exactly as
 * it would from the real API.
 */
export const resolveScenario = <TOutput>(contract: Contract<TOutput>, key: string): ResolvedScenario => {
  const scenario = contract.scenarios[key]
  if (!scenario) {
    throw new Error(`contract ${contract.operation}: unknown scenario "${key}"`)
  }

  const source = scenario.extends ? contract.scenarios[scenario.extends] : scenario
  const delayMs = scenario.delayMs ?? 0

  if (key === "network:offline") {
    return { networkError: "Simulated network failure (mock-api)", delayMs }
  }

  if (source.graphqlError) {
    const { code, message, status, instruction } = source.graphqlError
    return {
      body: {
        data: null,
        errors: [
          {
            message,
            extensions: {
              code,
              status,
              timestamp: "2024-01-01T00:00:00.000Z",
              ...(instruction ? { instruction } : {}),
            },
          },
        ],
      },
      delayMs,
    }
  }

  return { body: { data: wrapPayload(contract.field, source.payload?.()) }, delayMs }
}
